/**
 * MapView — provider-agnostic map abstraction.
 *
 * Backed by @vis.gl/react-google-maps (Google Maps Platform).
 * Previously backed by Leaflet + OpenStreetMap; migrated as part of #080.
 *
 * All consumers (CameraMapModal, DashboardPage, CamerasPage) remain unchanged —
 * the public MapMarker and MapViewProps interfaces are identical to the Leaflet era.
 *
 * MapMarker shape is intentionally provider-neutral.
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Map as GoogleMap, AdvancedMarker, MapControl, ControlPosition, RenderingType, useMap, useApiLoadingStatus, APILoadingStatus } from '@vis.gl/react-google-maps'
import { MarkerClusterer, SuperClusterAlgorithm, type Cluster, type Marker as ClusterMarker, type Renderer } from '@googlemaps/markerclusterer'
import { useTranslation } from 'react-i18next'
import { useMapConfig } from '@/hooks/useMapConfig'
import { cn } from '@/lib/utils'
import { cameraNumber } from '@/lib/cameraNumber'
import { CameraPinSvg, alertGeometry } from '@/components/map/CameraPinSvg'
import { PIN_COLOUR_HEX, PIN_SIZE_WIDTH, wedgeColour, wedgeDefaults, withDefaults } from '@/lib/mapSettings'
import type { MapSettings, PinShape, PinSize } from '@/types'
import type { MapCamera } from '@/lib/dashboardMapView'
import { PinHoverCard } from '@/components/map/PinHoverCard'
import { useHoverIntent } from '@/lib/hoverIntent'
import { useStreetViewSwitch } from '@/components/maps/useStreetViewSwitch'
import { ViewSwitch } from '@/components/maps/ViewSwitch'
import { PlusCode } from '@/components/maps/PlusCode'
import { WEDGE_MIN_ZOOM, bearingBetween, drawnRange, metresPerPx, normaliseHeading, tickPath, wedgePath, type WedgeDefaults } from '@/lib/mapDirection'
import { tickLengthPx, tickStyle, wedgeBand, wedgeStyles, type WedgeBand } from '@/lib/wedgeStyle'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface MapMarker {
  id: number
  lat: number
  lng: number
  label: string        // camera name
  sublabel?: string    // location text
  address?: string | null   // #247 Google address
  pulse?: boolean   // #247 fired just now; #306 alerting: 60 s from its camera's latest alert, or while its card is on screen
  thumbnailUrl?: string | null   // #247 last event thumbnail
  groupNames?: string[]
  isOnline?: boolean | null   // tri-state: true (online) / false (offline) / null (unknown — typically FTP cameras with no heartbeat protocol)
  lastEventType?: string | null
  lastEventAt?: string | null
  /** Where the pin is drawn when several cameras share a spot (set by spreadOverlapping).
   *  `lat`/`lng` stay the camera's true coordinates for the cards. */
  position?: { lat: number; lng: number }
  /** #330 — where the camera looks: heading 0–359°, width (°) and range (m); null or absent = not set.
   *  No heading, no wedge; a missing width or range takes the map settings' default (#339: 40°, 10 m unless a
   *  super admin changed it). */
  heading?: number | null
  fov?: number | null
  rangeM?: number | null
}

/** #330 — the direction being set for one camera, drawn in place of its stored one (in the preview style).
 *  Round 2 — with the width and range being edited: null takes the default (an emptied field saves as null),
 *  absent (the mouse-following pick) keeps the camera's own. */
export interface DirectionDraft { id: number; heading: number; fov?: number | null; rangeM?: number | null }

interface MapViewProps {
  markers: MapMarker[]
  /** Single-marker mode: fit map to this one pin. Multi-marker: fit bounds of all. */
  center?: { lat: number; lng: number }
  zoom?: number
  className?: string
  /** Called when a marker popup button is clicked (single-camera view) */
  onMarkerClick?: (id: number) => void
  /** Custom popup renderer — if provided, replaces the default popup content */
  renderPopup?: (marker: MapMarker) => React.ReactNode
  cluster?: boolean   // #247 group nearby pins (fleet map only)
  /** #247 — show the Map | Street View switch over the map, for this point (camera modal). */
  streetViewSwitch?: { lat: number; lng: number }
  /** #287 — a control drawn in the map's top left corner (the superuser's map settings gear). */
  topLeftControl?: React.ReactNode
  /** #279 — when given, pins can be dragged. Called on drop with the camera id and the new
   *  coordinates; the pin waits where it was dropped until the promise answers: `true` keeps it
   *  there (until the refreshed coordinates arrive), `false` snaps it back. */
  onMarkerMove?: (id: number, position: { lat: number; lng: number }) => Promise<boolean>
  /** #282 — the camera whose detail dialog is open; its pin shows the enlarged state. */
  selectedId?: number | null
  /** #290 — open here instead of fitting the pins, and keep this view until `fitSignal` changes. */
  initialView?: MapCamera
  /** #290 — changes each time the user edits the pin filter; ends the hold on `initialView`. */
  fitSignal?: number
  /** #290 — the centre and zoom, each time the map settles after a move or zoom. */
  onViewChange?: (view: MapCamera) => void
  /** #306 — when each alerting camera's newest alert arrived (ms, by camera id). A new value plays that
   *  pin's bounce again; kept apart from `markers` so a new alert does not rebuild them. The ripple, the
   *  ring and the glow (and a bubble's waves) run on regardless (#306 ripple). */
  alertStarts?: Record<number, number>
  /** #330 — when given (superusers only, like `onMarkerMove`), a pin's hover card offers "Set direction",
   *  which calls this with the camera id. */
  onSetDirection?: (id: number) => void
  /** #330 — the camera whose direction is being set: while it is, the map follows the mouse (bearing from that
   *  pin → `onDirectionMove`), a click picks it (`onDirectionPick`), the cursor is a crosshair, and the hover
   *  cards and pin clicks are off. */
  directionMode?: number | null
  onDirectionMove?: (id: number, heading: number) => void
  onDirectionPick?: (id: number, heading: number) => void
  /** #330 — drawn instead of that camera's stored heading, at any zoom (the mouse, then the confirm box). */
  directionDraft?: DirectionDraft | null
  /** #336 — a hover card's thumbnail failed (an expired link): the owner of the pins refreshes them once. */
  onThumbnailError?: () => void
  /** #369 — keep the google.maps.Map between mounts (the library parks it on unmount and hands it back to the next
   *  mount with the same map ID, engine and colour scheme): no new map load when the user comes back. Only for a map
   *  that mounts with the same options every time (the fleet map); a caller with other options must not share it. */
  reuseMaps?: boolean
}

// ── Spread overlapping markers ────────────────────────────────────────────────

// Spread markers that share the same coordinates so they're all visible
function spreadOverlapping(markers: MapMarker[]): MapMarker[] {
  const seen = new Map<string, number>()
  return markers.map(m => {
    const key = `${m.lat.toFixed(6)},${m.lng.toFixed(6)}`
    const count = seen.get(key) ?? 0
    seen.set(key, count + 1)
    if (count === 0) return m
    // Offset subsequent duplicates in a small circle (~30m radius)
    const angle = (count * 137.5 * Math.PI) / 180  // golden angle spread
    const offset = 0.0003
    // #336 — the copy reads the thumbnail link through its original: a link-only pins refresh keeps the marker and
    // writes the new link into it (PersistentDashboardMap), and the copy, made once per list, must show it too
    const original = m
    return {
      ...m,
      position: { lat: m.lat + offset * Math.sin(angle), lng: m.lng + offset * Math.cos(angle) },
      get thumbnailUrl() { return original.thumbnailUrl },
    }
  })
}

// ── Camera pin SVG ────────────────────────────────────────────────────────────

const pinPosition = (m: MapMarker) => m.position ?? { lat: m.lat, lng: m.lng }

// #282 — the pin itself lives in CameraPinSvg.tsx (shared with the event popup map).

// ── Hover card ────────────────────────────────────────────────────────────────

// #285 — the hover card lives in PinHoverCard.tsx (one width, whole thumbnail, never clipped).

/** #285 — z-index of a hovered, selected or dragged marker: above every other pin and above the
 *  cluster bubbles (1000 + count), so its card is never drawn over. */
const RAISED_PIN_Z = 100000
// #306 ripple — the sizes of a pin's alert come from alertGeometry (CameraPinSvg.tsx), which follows the pin's
// grown head for its shape and size (#287); nothing is sized here or in index.css.
const square = (px: number) => ({ width: px, height: px })

// ── Popup overlay ─────────────────────────────────────────────────────────────

function MarkerPopup({ marker, renderPopup }: { marker: MapMarker; renderPopup?: (m: MapMarker) => React.ReactNode }) {
  // #247 — theme tokens, as the hover card.
  return (
    <div
      data-map-card
      className="absolute bottom-10 left-1/2 z-10 min-w-[140px] -translate-x-1/2 rounded-md border border-border bg-popover px-3 py-2 text-popover-foreground shadow-md"
    >
      {renderPopup ? renderPopup(marker) : (
        <div className="min-w-[140px]">
          <p className="mb-0.5 text-[13px] font-semibold leading-snug">{marker.label}</p>
          {marker.sublabel && (
            <p className="mt-0.5 text-[11px] text-muted-foreground">{marker.sublabel}</p>
          )}
          {/* #246 */}
          <div className="mt-1">
            <PlusCode lat={marker.lat} lng={marker.lng} />
          </div>
          {marker.groupNames && marker.groupNames.length > 0 && (
            <p className="mt-0.5 text-[11px] text-muted-foreground/80">{marker.groupNames.join(', ')}</p>
          )}
        </div>
      )}
    </div>
  )
}

// ── Marker wrapper (handles hover tooltip vs click popup) ─────────────────────

// #330 fix round 1 — memoised: every prop MapView passes is stable (the markers are memoised, the callbacks are
// the caller's stable ones), so a change that is not this pin's (a direction draft following the mouse, the
// group circles) no longer redraws every pin. #342 — exported for its tests.
export const CameraMarker = memo(function CameraMarker({
  marker,
  onMarkerClick,
  renderPopup,
  onHandle,
  onMarkerMove,
  selected,
  settings,
  alertStart,
  onSetDirection,
  picking = false,
  onThumbnailError,
  thumbnailUrl,
  onActiveChange,
}: {
  marker: MapMarker
  onMarkerClick?: (id: number) => void
  renderPopup?: (m: MapMarker) => React.ReactNode
  /** #247 — reports the marker element so the clusterer can take it over. */
  onHandle?: (id: number, el: MarkerHandle | null) => void
  /** #279 — see MapViewProps. */
  onMarkerMove?: (id: number, position: { lat: number; lng: number }) => Promise<boolean>
  /** #282 — this camera's detail dialog is open. */
  selected?: boolean
  /** #287 — the company's map settings (shape, colour, size, offline mark, number). */
  settings: MapSettings
  /** #306 — when this camera's latest alert arrived, while its pin alerts. */
  alertStart?: number
  /** #330 — the hover card's "Set direction" (superusers only). */
  onSetDirection?: (id: number) => void
  /** #330 — a direction is being set on the map: no hover card, and a click on the pin does nothing. */
  picking?: boolean
  /** #336 — see MapViewProps. */
  onThumbnailError?: () => void
  /** #336 — the marker's thumbnail link as its own prop: a link-only pins refresh keeps the marker object (so the
   *  clusterer is not run again) and writes the new link into it; this prop is what tells the memo the link changed. */
  thumbnailUrl?: string | null
  /** #342 — told when the pin is hovered (or its card open), and when that ends or the pin goes away meanwhile, so
   *  the map can raise this camera's wedge. Not for a drag (the pin grows, but it is being moved, not looked at) nor
   *  for a selection (the map reads `selectedId` itself). Must be stable (the pin is memoised). */
  onActiveChange?: (id: number, active: boolean) => void
}) {
  const { t } = useTranslation('cameras')
  // #289 — the card follows the one hover standard (100 ms, warm, grace, Esc); the pin itself grows
  // the moment the pointer is over it, and stays grown while its card is open.
  const hover = useHoverIntent()
  const [over, setOver] = useState(false)
  const [popupOpen, setPopupOpen] = useState(false)
  // #282 — a drag in progress shows the same enlarged state as hover and selection.
  const [dragging, setDragging] = useState(false)
  // #279 — where the pin was dropped, while the caller decides (and until the camera's own
  // coordinates catch up after an accepted move). Null = the pin sits on the camera.
  const [dropped, setDropped] = useState<{ lat: number; lng: number } | null>(null)
  const mounted = useRef(true)
  useEffect(() => () => { mounted.current = false }, [])
  // The refreshed coordinates arrived (or the camera genuinely moved): the pin follows them again.
  useEffect(() => { setDropped(null) }, [marker.lat, marker.lng])
  // Stable, or React would detach and re-attach the element on every render.
  const handleRef = useCallback((el: MarkerHandle | null) => onHandle?.(marker.id, el), [onHandle, marker.id])

  function handleClick() {
    if (onMarkerClick) {
      onMarkerClick(marker.id)
    } else {
      setPopupOpen(open => !open)
    }
  }

  function handleDragEnd(e: google.maps.MapMouseEvent) {
    setDragging(false)
    if (!onMarkerMove) return
    const at = e.latLng
    if (!at) return   // the library reports no position: nothing moved
    const position = { lat: at.lat(), lng: at.lng() }
    setDropped(position)
    void onMarkerMove(marker.id, position).then((accepted) => {
      if (!accepted && mounted.current) setDropped(null)
    })
  }

  // #282 — status word for the accessible label; the colours are in CameraPinSvg.
  const status = t(marker.isOnline === true ? 'map.pin.online' : marker.isOnline === false ? 'map.pin.offline' : 'map.pin.unknown')
  const active = over || hover.open || dragging || !!selected
  // #342 — hovered (not dragged; a selection the map reads itself), reported on each change; the cleanup reports the
  // end, also when the pin goes away meanwhile
  const lookedAt = over || hover.open
  useEffect(() => {
    if (!lookedAt || !onActiveChange) return
    onActiveChange(marker.id, true)
    return () => onActiveChange(marker.id, false)
  }, [lookedAt, onActiveChange, marker.id])
  const pinWidth = PIN_SIZE_WIDTH[settings.pin_size]
  // #306 ripple — every size of the alert, for this pin's shape and size
  const alert = marker.pulse ? alertGeometry(settings.pin_shape, pinWidth) : null
  // #285 — the hover card is drawn at page level and measures this wrapper to place itself.
  const anchorRef = useRef<HTMLDivElement>(null)

  return (
    <AdvancedMarker
      ref={handleRef}
      position={dropped ?? pinPosition(marker)}
      // #285 — a click opens a dialog over the map and a drag moves the pin away; neither sends the
      // pin a mouseleave (the dialog blocks pointer events on the body), so the hover ends here.
      onClick={() => { setOver(false); hover.close(); if (!picking) handleClick() }}
      draggable={!!onMarkerMove}
      onDragStart={onMarkerMove ? () => { setOver(false); hover.close(); setDragging(true) } : undefined}
      onDragEnd={onMarkerMove ? handleDragEnd : undefined}
      // #285 — the active (hovered / selected / dragged) pin and its card above everything; #295 — an
      // alerting pin above the rest, but never over the active pin's card
      zIndex={active ? RAISED_PIN_Z + 1 : marker.pulse ? RAISED_PIN_Z : null}
    >
      <div
        ref={anchorRef}
        className={cn('detercam-pin', active && 'detercam-pin--active', marker.pulse && 'detercam-pin--alert')}
        // #306 ripple — the bounce heights for this pin size, read by the index.css keyframes
        style={{ position: 'relative', cursor: onMarkerMove ? 'grab' : 'pointer', ...(alert && ({ '--alert-bounce-1': `${alert.bounce[0]}px`, '--alert-bounce-2': `${alert.bounce[1]}px` } as React.CSSProperties)) }}
        onMouseEnter={() => { setOver(true); hover.enter() }}
        onMouseLeave={() => { setOver(false); hover.leave() }}
      >
        {/* #306 ripple (owner click-through 2026-10-12, "Ripple + steady ring") — a steady glow, a steady thin
            ring and one amber ripple that runs for the whole window, in the alert amber (index.css), centred on
            the head of every shape and size (#287) as the 1.25x alerting pin draws it; every size from
            alertGeometry. No key: a new alert must not restart the ripple (the image below replays the bounce). */}
        {alert && (
          <span aria-hidden className="detercam-pin-pulse" style={{ top: alert.top, ...square(alert.box) }}>
            <span className="detercam-pin-glow" style={square(alert.glow)} />
            <span className="detercam-pin-halo" style={{ ...square(alert.ring), borderWidth: alert.ringBorder }} />
            <span className="detercam-pin-ripple" style={{ borderWidth: alert.rippleBorder, '--alert-ripple-from': alert.rippleFrom } as React.CSSProperties} />
          </span>
        )}
        <CameraPinSvg
          // #306 — the alerting pin bounces once (index.css); a new alert draws the image anew, which
          // bounces it again. The wrapper, and a hover card in it, stay.
          key={marker.pulse ? `alert-${alertStart ?? ''}` : 'pin'}
          isOnline={marker.isOnline}
          number={cameraNumber(marker.label)}
          label={t('map.pin.ariaLabel', { name: marker.label, status })}
          shape={settings.pin_shape}
          colour={PIN_COLOUR_HEX[settings.pin_colour]}
          width={pinWidth}
          offlineMark={settings.offline_mark}
          showNumber={settings.show_number}
        />
        {hover.open && !popupOpen && !picking && (
          <PinHoverCard
            marker={marker}
            anchorRef={anchorRef}
            pinColour={PIN_COLOUR_HEX[settings.pin_colour]}
            instant={hover.instant}
            onLost={hover.close}
            onEnter={hover.enter}
            onLeave={hover.leave}
            onActivate={() => { setOver(false); hover.close(); handleClick() }}
            onSetDirection={onSetDirection ? () => { setOver(false); hover.close(); onSetDirection(marker.id) } : undefined}
            onThumbnailError={onThumbnailError}
            thumbnailUrl={thumbnailUrl}
          />
        )}
        {popupOpen && !onMarkerClick && (
          <MarkerPopup marker={marker} renderPopup={renderPopup} />
        )}
      </div>
    </AdvancedMarker>
  )
})

// ── Bounds fitter (must be a child of <Map> to use useMap) ───────────────────

// #247 — fit on the SET of pins, not on the array identity: a live refresh that changes a
// pin's last event must not throw the user back to the fleet view while they are zoomed in.
export const pinSetKey = (markers: MapMarker[]) => markers.map((m) => m.id).sort((a, b) => a - b).join(',')

function MapBoundsFitter({
  markers,
  explicitCenter,
  hold = false,
  fitSignal,
}: {
  markers: MapMarker[]
  explicitCenter?: { lat: number; lng: number }
  hold?: boolean
  fitSignal?: number
}) {
  const map = useMap()
  const key = pinSetKey(markers)
  const markersRef = useRef(markers)
  markersRef.current = markers
  // #290 — a restored view stays put while the pins and the restored filter arrive; the user's
  // first filter edit ends the hold, and the pins that edit shows are fitted as before. Declared
  // before the fit effect so an edit and its new pins in one render release first, then fit.
  const held = useRef(hold)
  const signalAtOpen = useRef(fitSignal)
  useEffect(() => {
    if (fitSignal !== signalAtOpen.current) held.current = false
  }, [fitSignal])
  useEffect(() => {
    if (!map) return
    if (explicitCenter) return  // caller wants a fixed center — don't override
    if (held.current) return
    const current = markersRef.current
    if (current.length === 0) return
    if (current.length === 1) {
      map.setCenter(pinPosition(current[0]))
      map.setZoom(15)
      return
    }
    const bounds = new google.maps.LatLngBounds()
    for (const m of current) {
      bounds.extend(pinPosition(m))
    }
    map.fitBounds(bounds, FIT_PADDING_PX)
  }, [map, key, explicitCenter])
  return null
}

// #369 — development only: the engine Google gave this map (VECTOR, or RASTER where the browser or the map ID
// cannot do vector), when the map mounts and again if Google changes it. The stage dashboard runs the dev server;
// production builds leave this component out.
function EngineLog() {
  const map = useMap()
  useEffect(() => {
    if (!map || typeof map.getRenderingType !== 'function') return
    const log = () => console.info('[map] engine', map.getRenderingType())
    log()
    const listener = typeof map.addListener === 'function' ? map.addListener('renderingtype_changed', log) : null
    return () => listener?.remove()
  }, [map])
  return null
}

// #369 — the library keeps this map for the next mount (reuseMaps), so nothing may stay on it. The clusterer, the
// wedges, the controls and the listeners already leave with their components; a Street View panorama the user opened
// with the pegman would not, so it is hidden here.
function ReuseCleanup() {
  const map = useMap()
  useEffect(() => {
    if (!map || typeof map.getStreetView !== 'function') return
    return () => { map.getStreetView().setVisible(false) }
  }, [map])
  return null
}

// #290 — tells the caller where the map is each time it settles (Google's 'idle').
function ViewReporter({ onViewChange }: { onViewChange: (view: MapCamera) => void }) {
  const map = useMap()
  const report = useRef(onViewChange)
  report.current = onViewChange
  useEffect(() => {
    if (!map) return
    const listener = map.addListener('idle', () => {
      const centre = map.getCenter()
      const zoom = map.getZoom()
      if (centre && zoom !== undefined) report.current({ center: { lat: centre.lat(), lng: centre.lng() }, zoom })
    })
    return () => listener.remove()
  }, [map])
  return null
}

// ── Direction wedges (#330) ───────────────────────────────────────────────────

// Owner 2026-10-13 (board A): a translucent wedge, no arrow, no label. Not clickable, so the map and the pins under
// it keep their clicks; the pins (AdvancedMarkers) are drawn on a layer above every shape.
// Round 2 (owner 2026-10-13 ~23:05 UTC) — on satellite imagery the 1 px edge in the pin's yellow was lost: a white
// halo under a 3 px edge, a 35 % fill, red by default (Map settings -> Wedge colour, lib/mapSettings wedgeColour).
// #342 (owner 2026-10-14) — a dark casing under the halo, bolder from zoom 17, and the hovered or selected camera's
// wedge raised with a static tick: every number is in lib/wedgeStyle.
const UNCLICKABLE = { clickable: false } as const
// A wedge being edited (the draft): a lighter fill and no solid edge, a dashed edge over the path instead; the halo
// and the casing stay (at the band's widths). Solid again when the draft ends.
const PREVIEW_STYLE = { fillOpacity: 0.15, strokeOpacity: 0 } as const
// #342 — over the wedge's fill, now that the wedge is the third polygon (zIndex 2)
const EDGE_STYLE = { strokeOpacity: 0, clickable: false, zIndex: 3 } as const
const dashes = (colour: string): google.maps.IconSequence[] => [
  { icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.9, strokeColor: colour, scale: 3 }, offset: '0', repeat: '12px' },
]
/** The dashed edge runs round the whole wedge: the polygon closes itself, a polyline has to be told. */
const closed = (path: google.maps.LatLngLiteral[]) => [...path, path[0]]

interface Wedge {
  casing: google.maps.Polygon   // #342
  halo: google.maps.Polygon
  main: google.maps.Polygon
  edge: google.maps.Polyline | null   // only while the wedge is a draft
  tick: google.maps.Polyline | null   // #342 — only while the wedge is raised and shown
  tickKey: string
  path: google.maps.LatLngLiteral[]
  pathKey: string
  band: WedgeBand   // #342 — the band and the raised state its polygons are styled for
  active: boolean
  colour: string
  preview: boolean
  shown: boolean
}

/** Three google.maps.Polygons per camera with a heading (or a draft), the dark casing under the white halo under the
 *  coloured wedge (#342), kept across renders and changed only when their shape, colour, style or visibility changes.
 *  Shown while the map is zoomed in to WEDGE_MIN_ZOOM or more and the camera is not inside a group circle; a draft
 *  shows at any zoom, with its own width and range and a dashed edge. A draft with a TYPED range is drawn at its true
 *  size; an empty range (following the default) keeps the minimum, and so does every other wedge: never drawn shorter
 *  than MIN_WEDGE_PX (round 2). The apex is where the pin is drawn. #339 — a null width or range is drawn with
 *  `defaults` (the map settings), and a change of them redraws those wedges. #342 — bolder from WEDGE_BOLD_ZOOM; the
 *  wedge of `activeId` (the hovered pin, or else the selected camera) is raised and shows a tick from the apex along
 *  the heading, past the active pin (`pinShape`, `pinSize`; lib/wedgeStyle tickLengthPx); never a draft's, which keeps
 *  its preview look. */
function DirectionWedges({ markers, colour, defaults, clusteredIds, draft, activeId, pinShape, pinSize }: {
  markers: MapMarker[]; colour: string; defaults: WedgeDefaults; clusteredIds: Set<number>; draft: DirectionDraft | null
  activeId: number | null; pinShape: PinShape; pinSize: PinSize
}) {
  const map = useMap()
  const [zoom, setZoom] = useState<number | undefined>(undefined)
  // #342 — the map's rotation (rotateControl): the pins stay upright on screen, so the tick measures the pin at the
  // heading's angle on screen, heading - map heading
  const [mapHeading, setMapHeading] = useState(0)
  useEffect(() => {
    if (!map || typeof map.addListener !== 'function') return
    const read = () => setZoom(map.getZoom())
    const readHeading = () => setMapHeading(map.getHeading?.() ?? 0)
    read()
    readHeading()
    const listeners = [map.addListener('zoom_changed', read), map.addListener('heading_changed', readHeading)]
    return () => { for (const listener of listeners) listener.remove() }
  }, [map])
  const wedges = useRef(new Map<number, Wedge>())
  useEffect(() => {
    if (!map) return
    const zoomedIn = zoom !== undefined && zoom >= WEDGE_MIN_ZOOM
    const band = wedgeBand(zoom ?? WEDGE_MIN_ZOOM)
    const kept = new Set<number>()
    for (const m of markers) {
      const drafted = draft?.id === m.id
      const heading = drafted ? draft.heading : m.heading
      if (typeof heading !== 'number') continue
      kept.add(m.id)
      // Round 2 — a draft's own width and range (null = the default); a draft without them keeps the camera's.
      // #339 — resolved here, before the path key, so a change of the default redraws the wedges that follow it.
      const fov = (drafted && draft.fov !== undefined ? draft.fov : m.fov) ?? defaults.fov
      const range = (drafted && draft.rangeM !== undefined ? draft.rangeM : m.rangeM) ?? defaults.rangeM
      const at = pinPosition(m)
      // Fix round (controller ruling I-3) — the wedge being edited in the confirm box or the sheet (a draft that
      // carries its range) is drawn at its true range, so a range edit shows at any zoom. Fix round 2 — the wedge
      // that follows the mouse while picking carries no range and keeps the 40 px minimum, as the stored wedges do.
      // #339 (controller ruling 2026-10-14) — so does a draft whose range is empty (null, the default): it is not
      // being edited and looks the same while editing as after Save. Only a typed range is drawn true.
      const trueRange = drafted && typeof draft.rangeM === 'number'
      const drawn = trueRange || zoom === undefined ? range : drawnRange(range, at.lat, zoom)
      const pathKey = [at.lat, at.lng, heading, fov, drawn].join(',')
      const shown = drafted || (zoomedIn && !clusteredIds.has(m.id))
      // #342 — a draft is never raised: it keeps its preview look and gets no tick
      const active = activeId === m.id && !drafted
      let wedge = wedges.current.get(m.id)
      if (!wedge) {
        const path = wedgePath(at, heading, fov, drawn)
        const style = wedgeStyles(band, active, colour)
        wedge = {
          casing: new google.maps.Polygon({ ...style.casing, ...UNCLICKABLE, paths: path }),
          halo: new google.maps.Polygon({ ...style.halo, ...UNCLICKABLE, paths: path }),
          main: new google.maps.Polygon({ ...style.main, ...UNCLICKABLE, paths: path }),
          edge: null, tick: null, tickKey: '', path, pathKey, band, active, colour, preview: false, shown: false,
        }
        wedges.current.set(m.id, wedge)
      } else if (shown && wedge.pathKey !== pathKey) {
        // a hidden wedge is not redrawn for every zoom step; it catches up when it shows again
        const path = wedgePath(at, heading, fov, drawn)
        wedge.casing.setPaths(path)
        wedge.halo.setPaths(path)
        wedge.main.setPaths(path)
        wedge.edge?.setPath(closed(path))
        wedge.path = path
        wedge.pathKey = pathKey
      }
      // #342 — restyled only when the band, the raised state, the colour or the draft changes: never on a zoom step
      // within a band. The casing and the halo follow the band and the raised state only.
      const ringsStale = wedge.band !== band || wedge.active !== active
      if (ringsStale || wedge.colour !== colour || wedge.preview !== drafted) {
        const style = wedgeStyles(band, active, colour)
        if (ringsStale) {
          wedge.casing.setOptions(style.casing)
          wedge.halo.setOptions(style.halo)
        }
        wedge.main.setOptions(drafted ? { ...style.main, ...PREVIEW_STYLE } : style.main)
        wedge.band = band
        wedge.active = active
      }
      if (wedge.colour !== colour) {
        wedge.edge?.setOptions({ icons: dashes(colour) })
        wedge.tick?.setOptions(tickStyle(colour))
        wedge.colour = colour
      }
      if (wedge.preview !== drafted) {
        if (drafted) {
          // a draft is always shown, so its edge goes straight onto the map
          wedge.edge = new google.maps.Polyline({ ...EDGE_STYLE, icons: dashes(colour), path: closed(wedge.path), map })
        } else {
          wedge.edge?.setMap(null)
          wedge.edge = null
        }
        wedge.preview = drafted
      }
      if (wedge.shown !== shown) {
        wedge.casing.setMap(shown ? map : null)
        wedge.halo.setMap(shown ? map : null)
        wedge.main.setMap(shown ? map : null)
        wedge.shown = shown
      }
      // #342 — the tick, while the wedge is raised and shown (so zoomed in to 15 or more): from the apex along the
      // heading, to 12 px past the active pin's outline that way on screen (an alerting pin is drawn larger), in metres
      // at this zoom. Static; gone when the wedge drops.
      if (active && shown && zoom !== undefined) {
        const tickPx = tickLengthPx(pinShape, pinSize, normaliseHeading(heading - mapHeading), !!m.pulse)
        const tickKey = [at.lat, at.lng, heading, tickPx, zoom].join(',')
        if (!wedge.tick || wedge.tickKey !== tickKey) {
          const tick = tickPath(at, heading, tickPx * metresPerPx(at.lat, zoom))
          if (wedge.tick) wedge.tick.setPath(tick)
          else wedge.tick = new google.maps.Polyline({ ...tickStyle(colour), ...UNCLICKABLE, path: tick, map })
          wedge.tickKey = tickKey
        }
      } else if (wedge.tick) {
        wedge.tick.setMap(null)
        wedge.tick = null
      }
    }
    // A heading cleared (or a draft cancelled on a camera without one), or a camera gone from the map
    for (const [id, wedge] of wedges.current) {
      if (kept.has(id)) continue
      removeWedge(wedge)
      wedges.current.delete(id)
    }
  }, [map, markers, colour, defaults.fov, defaults.rangeM, clusteredIds, draft, zoom, mapHeading, activeId, pinShape, pinSize])
  useEffect(() => () => {
    for (const wedge of wedges.current.values()) removeWedge(wedge)
    wedges.current.clear()
  }, [])
  return null
}

function removeWedge(wedge: Wedge) {
  wedge.casing.setMap(null)
  wedge.halo.setMap(null)
  wedge.main.setMap(null)
  wedge.edge?.setMap(null)
  wedge.tick?.setMap(null)
}

/** While a superuser sets a camera's direction: the bearing from its pin follows the mouse, a click picks it
 *  (whole degrees), the cursor is a crosshair, and a short hint sits at the top of the map. Lives inside the map
 *  (useMap): the fleet map's parent renders outside it, where useMap would answer with whichever map is 'default'. */
function DirectionPicker({ marker, onMove, onPick }: {
  marker: MapMarker
  onMove?: (id: number, heading: number) => void
  onPick?: (id: number, heading: number) => void
}) {
  const map = useMap()
  const { t } = useTranslation('cameras')
  const moveRef = useRef(onMove)
  moveRef.current = onMove
  const pickRef = useRef(onPick)
  pickRef.current = onPick
  const { id } = marker
  const { lat, lng } = pinPosition(marker)
  useEffect(() => {
    if (!map || typeof map.addListener !== 'function') return
    const headingTo = (e: google.maps.MapMouseEvent) =>
      e.latLng ? Math.round(bearingBetween({ lat, lng }, { lat: e.latLng.lat(), lng: e.latLng.lng() })) % 360 : null
    const move = map.addListener('mousemove', (e: google.maps.MapMouseEvent) => {
      const heading = headingTo(e)
      if (heading !== null) moveRef.current?.(id, heading)
    })
    const click = map.addListener('click', (e: google.maps.MapMouseEvent) => {
      e.stop?.()   // belt and braces: Google's place icons are not clickable on this map (#369)
      const heading = headingTo(e)
      if (heading !== null) pickRef.current?.(id, heading)
    })
    map.setOptions({ draggableCursor: 'crosshair' })
    return () => {
      move.remove()
      click.remove()
      map.setOptions({ draggableCursor: null })
    }
  }, [map, id, lat, lng])
  return (
    <MapControl position={ControlPosition.TOP_CENTER}>
      <p role="status" className="mt-2 rounded-md border border-border bg-background/95 px-2 py-1 text-xs text-foreground shadow-md backdrop-blur-sm">
        {t('map.direction.hint')}
      </p>
    </MapControl>
  )
}

// ── Street View switch (#247) ─────────────────────────────────────────────────

// Rendered as a child of <Map>, in Google's own control slots: the switch lands top-left BESIDE
// Google's map-type control (which the modal keeps), and the note bottom-centre ABOVE the attribution.
function StreetViewOverlay({ at }: { at: { lat: number; lng: number } }) {
  const map = useMap()
  const sv = useStreetViewSwitch(map, at)
  const { t } = useTranslation('events')
  useEffect(() => () => { sv.cancel(); map?.getStreetView().setVisible(false) }, [map])  // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <MapControl position={ControlPosition.TOP_LEFT}>
        <ViewSwitch
          view={sv.view}
          checking={sv.checking}
          disabled={!sv.ready}
          onMap={sv.showMap}
          onStreetView={sv.showStreetView}
          className="relative left-0 top-0 m-2"
        />
      </MapControl>
      {sv.unavailable && (
        <MapControl position={ControlPosition.BOTTOM_CENTER}>
          <p role="status" className="mb-2 rounded-md border border-border bg-background/95 px-2 py-1 text-xs text-muted-foreground shadow-md backdrop-blur-sm">
            {t('streetView.notAvailable')}
          </p>
        </MapControl>
      )}
    </>
  )
}

// ── Clustering (#247) ─────────────────────────────────────────────────────────

const CLUSTER_MAX_ZOOM = 16   // clusters break up at zoom 17
const CLUSTER_RADIUS_PX = 60
const CLUSTER_CLICK_MAX_ZOOM = 18   // #369 — a click on a circle never ends closer than this
const FIT_PADDING_PX = 60   // the margin round a fitted set of pins, and round a clicked circle's cameras (#369)
type MarkerHandle = google.maps.marker.AdvancedMarkerElement
// The renderer only sees marker elements; this is how it learns each camera's status.
const statusByHandle = new WeakMap<object, boolean | null>()
// ...and whether the camera behind an element fired just now, for the cluster that holds it.
const pulseByHandle = new WeakMap<object, boolean>()
// ...and the camera itself, for the cluster's hover card.
const markerByHandle = new WeakMap<object, MapMarker>()

// A cluster bubble pulses while any camera inside it does. The clusterer keeps its clusters on
// a protected field and rebuilds the bubbles on every map idle, so this runs after each render.
// #306 ripple — the class alone draws the steady amber ring and the continuous waves (index.css,
// `.detercam-cluster--pulse` and its ::before): nothing is added to the bubble, so nothing can be left behind
// when the library keeps a bubble past its alert (markerclusterer 2.6 does when nothing moved).
function applyClusterPulse(clusterer: MarkerClusterer | null) {
  const clusters = (clusterer as unknown as { clusters?: { markers: ClusterMarker[]; marker?: ClusterMarker }[] } | null)?.clusters
  if (!clusters) return
  for (const cluster of clusters) {
    if (cluster.markers.length < 2) continue   // a lone pin wears its own ring
    const content = (cluster.marker as google.maps.marker.AdvancedMarkerElement | undefined)?.content
    if (!(content instanceof HTMLElement)) continue
    content.classList.toggle('detercam-cluster--pulse', cluster.markers.some((m) => pulseByHandle.get(m) === true))
  }
}

// #330 — the cameras inside a group circle (2 or more pins), read from the clusterer's clusters as above; their
// wedges are hidden while the circle stands. Above CLUSTER_MAX_ZOOM every camera is alone.
function clusteredCameraIds(clusterer: MarkerClusterer | null): Set<number> {
  const clusters = (clusterer as unknown as { clusters?: { markers: ClusterMarker[] }[] } | null)?.clusters
  const ids = new Set<number>()
  for (const cluster of clusters ?? []) {
    if (cluster.markers.length < 2) continue
    for (const m of cluster.markers) {
      const camera = markerByHandle.get(m)
      if (camera) ids.add(camera.id)
    }
  }
  return ids
}

type ClusterHover = { content: HTMLElement; markers: ClusterMarker[] }

// The bubble is a plain element built outside React; it reports hover so ClusterManager can show
// a card in it, as a single pin shows one. The click is ClusterManager's own handler (#369: a capped fly-in).
function makeClusterRenderer(onHover: (hover: ClusterHover | null) => void, showOfflineMark: () => boolean = () => true): Renderer {
  return {
    render: ({ count, position, markers }) => {
      const members = markers ?? []
      const offline = members.some((m) => statusByHandle.get(m) === false)
      const content = document.createElement('div')
      content.className = 'detercam-cluster' + (offline && showOfflineMark() ? ' detercam-cluster--offline' : '')   // #287
      content.textContent = String(count)
      content.addEventListener('mouseenter', () => onHover({ content, markers: members }))
      content.addEventListener('mouseleave', () => onHover(null))
      return new google.maps.marker.AdvancedMarkerElement({ position, content, zIndex: 1000 + count })
    },
  }
}

// How many camera names the cluster card lists before "+N more".
const CLUSTER_CARD_NAMES = 8

function ClusterTooltip({ markers, pinColour, instant = false }: { markers: MapMarker[]; pinColour: string; instant?: boolean }) {
  const { t } = useTranslation('cameras')
  const offline = markers.filter((m) => m.isOnline === false).length
  const shown = markers.slice(0, CLUSTER_CARD_NAMES)
  const more = markers.length - shown.length
  return (
    <div
      data-map-card
      data-instant={instant ? '' : undefined}
      // #289 — the shared hover look and motion; it takes the pointer, and its click reaches the
      // bubble, which zooms in as the hint says
      className="hover-surface hover-motion absolute bottom-12 left-1/2 z-50 w-max min-w-[180px] max-w-[280px] -translate-x-1/2 cursor-pointer px-3.5 py-2.5 text-left"
    >
      <p className="m-0 mb-1.5 flex items-center gap-2 text-[13px] font-bold leading-snug text-foreground">
        <span>{t('map.cluster.cameras', { count: markers.length })}</span>
        {offline > 0 && (
          // #286 — red is the one signal for offline
          <span className="text-[11px] font-semibold text-red-600 dark:text-red-400">{t('map.cluster.offline', { count: offline })}</span>
        )}
      </p>
      <ul className="m-0 list-none p-0">
        {shown.map((m) => (
          <li key={m.id} data-cluster-camera className="flex items-center gap-1.5 text-xs font-normal leading-snug text-muted-foreground">
            {/* #286 — the pin's marks: yellow for every camera, red for an offline one */}
            {/* #287 — the pin colour (outlined so white reads); red for an offline camera */}
            <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', m.isOnline === false ? 'bg-red-500' : 'ring-1 ring-slate-800/70')} style={m.isOnline === false ? undefined : { backgroundColor: pinColour }} />
            <span className="truncate">{m.label}</span>
          </li>
        ))}
      </ul>
      {more > 0 && <p className="m-0 mt-1 text-[11px] font-normal text-muted-foreground/70">{t('map.cluster.more', { count: more })}</p>}
      <p className="m-0 mt-1.5 border-t border-border pt-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">{t('map.cluster.clickToZoom')}</p>
      <span className="hover-arrow -bottom-1.5 left-1/2 -translate-x-1/2 border-b border-r" />
    </div>
  )
}

// `handles` is React state (not a ref): the real AdvancedMarker creates its element in an effect
// and forwards it one render later, so the clusterer must re-run when the elements arrive.
function ClusterManager({ markers, handles, showOfflineMark, pinColour, onClustered, picking = false }: {
  markers: MapMarker[]; handles: Map<number, MarkerHandle>; showOfflineMark: boolean; pinColour: string
  /** #330 — after each clustering, the ids of the cameras inside a group circle. */
  onClustered?: (ids: Set<number>) => void
  /** #330 — a direction is being set: no cluster card, as no pin card. */
  picking?: boolean
}) {
  const map = useMap()
  const onClusteredRef = useRef(onClustered)
  onClusteredRef.current = onClustered
  // #287 — read by the renderer below, which the clusterer keeps; a ref so a saved change applies on the next clustering
  const showOfflineMarkRef = useRef(showOfflineMark)
  showOfflineMarkRef.current = showOfflineMark
  const clustererRef = useRef<MarkerClusterer | null>(null)
  const listenerRef = useRef<google.maps.MapsEventListener | null>(null)
  const key = pinSetKey(markers)
  // #369 — a click on a circle flies to its cameras with a margin and stops at 18 (two neighbours would otherwise end
  // at street level). The cap is the map's maxZoom while the move runs, so the fit lands at 18 with no bounce back;
  // the previous value returns when the map settles, or at once if this component unmounts first (review I1: with
  // reuseMaps the map comes back, and must not come back capped). One restore pending at a time: a second click before
  // the settle keeps the first click's "previous", never the 18 it set.
  const capRef = useRef<{ map: google.maps.Map; previous: number | null; restore: google.maps.MapsEventListener } | null>(null)
  const onClusterClick = useCallback((_e: google.maps.MapMouseEvent, cluster: Cluster, m: google.maps.Map) => {
    if (!cluster.bounds) return
    const previous = capRef.current ? capRef.current.previous : currentMaxZoom(m)
    capRef.current?.restore.remove()
    m.setOptions({ maxZoom: CLUSTER_CLICK_MAX_ZOOM })
    const restore = google.maps.event.addListenerOnce(m, 'idle', () => {
      capRef.current = null
      m.setOptions({ maxZoom: previous })
    })
    capRef.current = { map: m, previous, restore }
    m.fitBounds(cluster.bounds, FIT_PADDING_PX)
  }, [])
  // The hovered bubble and the cameras inside it; the card is portalled into the bubble.
  // #289 — opened by the one hover standard; `swap` = the pointer moved here from another bubble
  // while the card was open, so the card shows here without the enter motion.
  const hover = useHoverIntent()
  const hoverRef = useRef(hover)
  hoverRef.current = hover
  const [target, setTarget] = useState<{ content: HTMLElement; markers: MapMarker[]; swap: boolean } | null>(null)
  const renderer = useMemo(
    () =>
      makeClusterRenderer((h) => {
        if (!h) { hoverRef.current.leave(); return }
        setTarget({ content: h.content, markers: h.markers.map((m) => markerByHandle.get(m)).filter((m): m is MapMarker => !!m), swap: hoverRef.current.open })
        hoverRef.current.enter()
      }, () => showOfflineMarkRef.current),
    [],
  )
  useEffect(() => {
    if (!map) return
    try {
      if (!clustererRef.current) {
        clustererRef.current = new MarkerClusterer({
          map, markers: [], renderer, onClusterClick,
          algorithm: new SuperClusterAlgorithm({ maxZoom: CLUSTER_MAX_ZOOM, radius: CLUSTER_RADIUS_PX }),
        })
        // Bubbles are rebuilt on every clustering: re-apply the pulse, and drop a card whose bubble is gone.
        listenerRef.current = google.maps.event.addListener(clustererRef.current, 'clusteringend', () => {
          applyClusterPulse(clustererRef.current); hoverRef.current.close(); setTarget(null)
          onClusteredRef.current?.(clusteredCameraIds(clustererRef.current))   // #330
        })
      }
      const list: ClusterMarker[] = []
      for (const m of markers) {
        const h = handles.get(m.id)
        if (!h) continue
        statusByHandle.set(h, m.isOnline ?? null)
        pulseByHandle.set(h, !!m.pulse)
        markerByHandle.set(h, m)
        list.push(h)
      }
      clustererRef.current.clearMarkers(true)
      clustererRef.current.addMarkers(list)
      applyClusterPulse(clustererRef.current)
    } catch (e) {
      // Unexpected Google change: log, and leave the pins exactly as they were, unclustered.
      console.error('[MapView] clustering unavailable, pins shown unclustered', e)
      clustererRef.current = null
    }
  }, [map, key, markers, handles, renderer, onClusterClick, showOfflineMark])   // #287 — a saved offline mark repaints the bubbles
  useEffect(() => () => {
    listenerRef.current?.remove(); clustererRef.current?.setMap(null); clustererRef.current = null
    const cap = capRef.current   // #369 — a cap mid-flight: drop the pending restore and write the previous value back now
    if (cap) { cap.restore.remove(); cap.map.setOptions({ maxZoom: cap.previous }); capRef.current = null }
  }, [])
  return hover.open && target && !picking
    ? createPortal(<ClusterTooltip markers={target.markers} pinColour={pinColour} instant={hover.instant || target.swap} />, target.content)
    : null
}

// ── Main component ────────────────────────────────────────────────────────────

// #302 — Google's 'auto' asks for Ctrl + wheel as soon as the page can scroll, so the map stopped zooming on the
// plain wheel when the dashboard grew. A mouse or trackpad gets 'greedy' (the wheel always zooms the map); a touch
// screen keeps 'auto', where one finger scrolls the page and two move the map.
function wheelGesture(): 'greedy' | 'auto' {
  const coarse = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(pointer: coarse)').matches
  return coarse ? 'auto' : 'greedy'
}

// #369 — the map's maxZoom option as set now (null = no cap), read back through the MVCObject getter (`get`, long
// standing on google.maps.Map though not in its documented surface). Anything but a number counts as no cap, which is
// this map's state anyway: nothing in the dashboard sets maxZoom.
function currentMaxZoom(map: google.maps.Map): number | null {
  const value = typeof map.get === 'function' ? (map.get('maxZoom') as unknown) : undefined
  return typeof value === 'number' ? value : null
}

export function MapView({ markers, center, zoom, className = '', onMarkerClick, renderPopup, cluster = false, streetViewSwitch, onMarkerMove, selectedId = null, topLeftControl, initialView, fitSignal, onViewChange, alertStarts, onSetDirection, directionMode = null, onDirectionMove, onDirectionPick, directionDraft = null, onThumbnailError, reuseMaps }: MapViewProps) {
  const { data: config, isLoading } = useMapConfig()
  // #287 — the company's map settings arrive with the config; missing keys take the defaults
  const settings = useMemo(() => withDefaults(config?.map_settings), [config?.map_settings])
  // #279 — the provider's own status is the source of truth for "the API provider is mounted
  // above us". MapsApiProvider wraps the tree only once the same config query resolves; when
  // the config and the pins arrive in the same tick this component can re-render (from the
  // pins) with the cached config BEFORE the provider has re-rendered, and the real <Map> then
  // throws "can only be used inside an <ApiProvider>" — taking the whole page into the error
  // boundary. Outside a provider the status is NOT_LOADED, so we keep the placeholder until
  // the provider reports anything else. Google configs only: for any other provider (the
  // MAP_PROVIDER kill-switch) no API provider ever mounts, and the message below must show.
  const apiStatus = useApiLoadingStatus()
  const spreadMarkers = useMemo(() => spreadOverlapping(markers), [markers])
  // #247 — marker elements by camera id, for the clusterer. State, so the clusterer re-runs when
  // the elements arrive; the setter returns the previous map when nothing changed.
  const [handles, setHandles] = useState<Map<number, MarkerHandle>>(() => new Map())
  const onHandle = useCallback((id: number, el: MarkerHandle | null) => {
    setHandles((prev) => {
      if (el ? prev.get(id) === el : !prev.has(id)) return prev
      const next = new Map(prev)
      if (el) next.set(id, el)
      else next.delete(id)
      return next
    })
  }, [])
  // #330 — the cameras inside a group circle, from the clusterer (their wedges wait until the circle breaks up).
  // State, and replaced only when the set changes, so a clustering that changes nothing renders nothing.
  const [clustered, setClustered] = useState<Set<number>>(() => new Set())
  const onClustered = useCallback((ids: Set<number>) => {
    setClustered((prev) => (prev.size === ids.size && [...ids].every((id) => prev.has(id)) ? prev : ids))
  }, [])
  // #342 — the pins that report themselves hovered, the latest last; stable, as the pins are memoised
  const [activePins, setActivePins] = useState<number[]>([])
  const onActiveChange = useCallback((id: number, active: boolean) => {
    setActivePins((prev) => {
      const rest = prev.filter((p) => p !== id)
      if (active) return [...rest, id]
      return rest.length === prev.length ? prev : rest
    })
  }, [])

  if (isLoading || !config || (config.provider === 'google' && apiStatus === APILoadingStatus.NOT_LOADED)) {
    return (
      <div
        className={className}
        style={{ minHeight: 300, background: '#f3f4f6', borderRadius: '0.5rem' }}
      />
    )
  }

  if (config.provider !== 'google') {
    return (
      <div
        className={className}
        style={{
          minHeight: 300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f3f4f6',
          color: '#6b7280',
          borderRadius: '0.5rem',
        }}
      >
        Map temporarily unavailable.
      </div>
    )
  }

  // #330 — the wedge layer only when there is something to draw (most maps have no heading at all).
  // Fix round 1 (review I-1) — a mode whose camera is not on the map (a filter edit, a status change) is inert:
  // no hint, no crosshair, no click handler, and the other pins' cards and clicks work as usual.
  const pickedMarker = directionMode !== null ? spreadMarkers.find((m) => m.id === directionMode) : undefined
  const picking = !!pickedMarker
  const hasWedges = !!directionDraft || spreadMarkers.some((m) => typeof m.heading === 'number')
  // #342 — the raised wedge: the latest hovered pin with a wedge to raise (a heading, and not the draft's, which keeps
  // its preview look), or else the selected camera; a hover wins over the selection. While a direction is being picked
  // nothing is raised: the hover cards are off, the mouse sweeps across the map, and no raised neighbour may draw over
  // the draft's dashed edge and fill.
  const hasRaisableWedge = (id: number) =>
    id !== directionDraft?.id && spreadMarkers.some((m) => m.id === id && typeof m.heading === 'number')
  const hoveredWedgeId = [...activePins].reverse().find(hasRaisableWedge)
  const activeWedgeId = picking ? null : hoveredWedgeId ?? selectedId

  return (
    <GoogleMap
      className={className}
      style={{ width: '100%', height: '100%', minHeight: 400 }}
      defaultCenter={initialView?.center ?? center ?? config.default_center}
      defaultZoom={initialView?.zoom ?? zoom ?? config.default_zoom}
      mapTypeId={settings.map_style}   // #287
      mapId="DETERCAM_MAP"
      // #369 — the raster engine, as before this item: the vector engine (tried on stage 2026-10-21) felt slower on the
      // owner's laptop, and the client's machine is unknown. Stated explicitly, not left to the default, because the
      // library's reuse cache key is mapId:renderingType:colourScheme: a kept fleet map (reuseMaps) must never share a
      // key with the event popup map, which also keeps its instance with this map ID and no engine set. No tilt or
      // camera options: the hybrid map keeps its oblique imagery and rotate control at close zoom, as today.
      renderingType={RenderingType.RASTER}
      reuseMaps={reuseMaps}
      gestureHandling={wheelGesture()}   // #302
      disableDefaultUI={false}
      mapTypeControl={true}
      streetViewControl={settings.street_view}   // #287
      fullscreenControl={true}
      zoomControl={true}
      scaleControl={true}
      rotateControl={true}
      clickableIcons={false}   // #369 — Google's shop and station icons no longer open Google's bubble over our pins
    >
      {import.meta.env.DEV && <EngineLog />}
      {reuseMaps && <ReuseCleanup />}
      <MapBoundsFitter markers={spreadMarkers} explicitCenter={center} hold={!!initialView} fitSignal={fitSignal} />
      {onViewChange && <ViewReporter onViewChange={onViewChange} />}
      {cluster && <ClusterManager markers={spreadMarkers} handles={handles} showOfflineMark={settings.offline_mark !== 'none'} pinColour={PIN_COLOUR_HEX[settings.pin_colour]} onClustered={onClustered} picking={picking} />}
      {hasWedges && <DirectionWedges markers={spreadMarkers} colour={wedgeColour(settings)} defaults={wedgeDefaults(settings)} clusteredIds={clustered} draft={directionDraft} activeId={activeWedgeId} pinShape={settings.pin_shape} pinSize={settings.pin_size} />}
      {pickedMarker && <DirectionPicker marker={pickedMarker} onMove={onDirectionMove} onPick={onDirectionPick} />}
      {/* #247 — Street View switch and its note, in Google's control slots (camera modal) */}
      {streetViewSwitch && <StreetViewOverlay at={streetViewSwitch} />}
      {topLeftControl && <MapControl position={ControlPosition.TOP_LEFT}>{topLeftControl}</MapControl>}
      {spreadMarkers.map(marker => (
        <CameraMarker
          key={marker.id}
          marker={marker}
          onMarkerClick={onMarkerClick}
          renderPopup={renderPopup}
          onHandle={cluster ? onHandle : undefined}
          // #330 fix round 1 — the pins stay still while a direction is being set (no #279 drag from the pin)
          onMarkerMove={picking ? undefined : onMarkerMove}
          selected={selectedId === marker.id}
          settings={settings}
          alertStart={alertStarts?.[marker.id]}
          onSetDirection={onSetDirection}
          picking={picking}
          onThumbnailError={onThumbnailError}
          thumbnailUrl={marker.thumbnailUrl}  // #336 — read now: the memo sees a link written into a kept marker
          // #342 — only where there are wedges to raise: elsewhere a hover does not render the map again
          onActiveChange={hasWedges ? onActiveChange : undefined}
        />
      ))}
    </GoogleMap>
  )
}
